import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { 
  ArrowLeft,
  MapPin, 
  Compass, 
  Play,
  BookOpen,
  Map,
  Clock,
  Volume2,
  Users,
  Sun,
  ChevronDown,
  CheckCircle,
  AlertTriangle,
  Info,
  Sparkles
} from 'lucide-react';
import { motion } from 'framer-motion';

const ExperienceViewer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [experience, setExperience] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [embedErrors, setEmbedErrors] = useState({});
  
  // Build My Visit state
  const [sensitivities, setSensitivities] = useState({
    noise: 'medium',
    crowd: 'medium',
    lighting: 'medium'
  });
  const [recommendations, setRecommendations] = useState(null);

  // Refs for scrolling
  const mapRef = useRef(null);
  const tourRef = useRef(null);
  const guideRef = useRef(null);
  const buildRef = useRef(null);

  useEffect(() => {
    fetchExperience();
  }, [id]);

  const fetchExperience = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/public/experience/${id}`);
      const data = await response.json();
      setExperience(data);
    } catch (error) {
      console.error('Failed to load experience:', error);
      setExperience({ not_found: true, message: 'Unable to load this experience.' });
    } finally {
      setLoading(false);
    }
  };

  const scrollToSection = (ref, section) => {
    setActiveSection(section);
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const generateRecommendations = () => {
    if (!experience || !experience.areas) return;

    const recs = {
      bestTimes: [],
      cautionAreas: [],
      recommendedAreas: [],
      tips: []
    };

    // Time recommendations based on sensitivities
    if (sensitivities.noise === 'high' || sensitivities.crowd === 'high') {
      recs.bestTimes.push('Weekday mornings (10am-12pm)');
      recs.bestTimes.push('Late afternoon (3pm-4:30pm)');
      recs.tips.push('Avoid weekend peak hours (11am-3pm)');
    } else if (sensitivities.noise === 'medium' || sensitivities.crowd === 'medium') {
      recs.bestTimes.push('Weekday afternoons');
      recs.bestTimes.push('Early weekends');
    } else {
      recs.bestTimes.push('Any time works well for you');
    }

    // Area recommendations based on sensitivities
    experience.areas.forEach(area => {
      const isQuiet = area.noise_level === 'low';
      const isCalm = area.crowd_level === 'low';
      const isComfortable = area.lighting_level === 'comfortable' || area.lighting_level === 'natural';

      // Check if this area matches user's needs
      let concerns = [];
      if (sensitivities.noise === 'high' && area.noise_level === 'high') {
        concerns.push('noise');
      }
      if (sensitivities.crowd === 'high' && area.crowd_level === 'high') {
        concerns.push('crowds');
      }
      if (sensitivities.lighting === 'high' && (area.lighting_level === 'bright' || area.lighting_level === 'challenging')) {
        concerns.push('lighting');
      }

      if (concerns.length > 0) {
        recs.cautionAreas.push({
          name: area.name,
          concerns: concerns
        });
      } else if (isQuiet || isCalm) {
        recs.recommendedAreas.push(area.name);
      }
    });

    // General tips
    if (sensitivities.noise === 'high') {
      recs.tips.push('Consider bringing noise-reducing headphones');
    }
    if (sensitivities.crowd === 'high') {
      recs.tips.push('Plan your route to avoid busy areas during peak times');
    }
    if (sensitivities.lighting === 'high') {
      recs.tips.push('Sunglasses may help in brighter outdoor areas');
    }

    setRecommendations(recs);
  };

  const handleEmbedError = (embedType) => {
    setEmbedErrors(prev => ({ ...prev, [embedType]: true }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading experience...</p>
        </div>
      </div>
    );
  }

  if (experience?.not_found) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-6">
            <Info className="w-8 h-8 text-slate-400" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-3">Experience Not Available</h1>
          <p className="text-slate-600 mb-6">{experience.message}</p>
          <Button onClick={() => navigate('/explore')} className="rounded-full">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Explore
          </Button>
        </div>
      </div>
    );
  }

  const venue = experience?.venue || {};
  const embeds = experience?.embeds || {};
  const visitRecs = experience?.visit_recommendations || {};

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/explore')}
            className="text-slate-600 -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Link to="/explore" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center">
              <Compass className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold text-slate-800 hidden sm:block">Nysa</span>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-600 to-emerald-700 text-white py-12 md:py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {venue.venue_type && (
              <span className="inline-block bg-white/20 px-3 py-1 rounded-full text-sm mb-4">
                {venue.venue_type}
              </span>
            )}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              {venue.name || 'Venue Experience'}
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto mb-8">
              {venue.description || 'Explore this venue with full sensory accessibility information.'}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                size="lg"
                onClick={() => scrollToSection(buildRef, 'build')}
                className="bg-white text-teal-700 hover:bg-white/90 rounded-full px-8 h-12"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Build my visit
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => scrollToSection(mapRef, 'map')}
                className="border-white/40 text-white hover:bg-white/10 rounded-full px-8 h-12"
              >
                <Map className="w-5 h-5 mr-2" />
                View map
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-slate-200 sticky top-[52px] z-40">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto py-2 -mx-4 px-4 scrollbar-hide">
            {[
              { id: 'map', label: 'Map', icon: Map, ref: mapRef },
              { id: 'tour', label: 'Virtual Tour', icon: Play, ref: tourRef },
              { id: 'guide', label: 'Guide', icon: BookOpen, ref: guideRef },
              { id: 'build', label: 'Build Visit', icon: Sparkles, ref: buildRef },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => scrollToSection(tab.ref, tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  activeSection === tab.id
                    ? 'bg-teal-100 text-teal-700'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8 space-y-12">
        
        {/* Map Section */}
        <section ref={mapRef} className="scroll-mt-32">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
              <Map className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Interactive Map</h2>
              <p className="text-sm text-slate-600">Tap areas to explore different parts of the venue</p>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            {embeds.kuula_map && !embedErrors.map ? (
              <div className="aspect-[4/3] md:aspect-[16/9]">
                <iframe
                  src={embeds.kuula_map}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  allow="xr-spatial-tracking; gyroscope; accelerometer"
                  allowFullScreen
                  scrolling="no"
                  title="Interactive Map"
                  onError={() => handleEmbedError('map')}
                  className="w-full h-full"
                />
              </div>
            ) : (
              <div className="aspect-[4/3] md:aspect-[16/9] bg-slate-100 flex items-center justify-center">
                <div className="text-center p-8">
                  <Map className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Interactive map coming soon</p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Virtual Tour Section */}
        <section ref={tourRef} className="scroll-mt-32">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <Play className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Virtual Tour</h2>
              <p className="text-sm text-slate-600">Move through the space before you arrive</p>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            {embeds.kuula_tour && !embedErrors.tour ? (
              <div className="aspect-[4/3] md:aspect-[16/9]">
                <iframe
                  src={embeds.kuula_tour}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  allow="xr-spatial-tracking; gyroscope; accelerometer"
                  allowFullScreen
                  scrolling="no"
                  title="360 Virtual Tour"
                  onError={() => handleEmbedError('tour')}
                  className="w-full h-full"
                />
              </div>
            ) : (
              <div className="aspect-[4/3] md:aspect-[16/9] bg-slate-100 flex items-center justify-center">
                <div className="text-center p-8">
                  <Play className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Virtual tour coming soon</p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Guide Section */}
        <section ref={guideRef} className="scroll-mt-32">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Sensory Guide</h2>
              <p className="text-sm text-slate-600">Detailed accessibility information for each area</p>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            {embeds.flipsnack_guide && !embedErrors.guide ? (
              <div className="aspect-[3/4] md:aspect-[4/3]">
                <iframe
                  src={embeds.flipsnack_guide}
                  width="100%"
                  height="100%"
                  seamless="seamless"
                  scrolling="no"
                  frameBorder="0"
                  allowFullScreen
                  allow="autoplay; clipboard-read; clipboard-write"
                  title="Sensory Guide"
                  onError={() => handleEmbedError('guide')}
                  className="w-full h-full"
                />
              </div>
            ) : (
              <div className="aspect-[3/4] md:aspect-[4/3] bg-slate-100 flex items-center justify-center">
                <div className="text-center p-8">
                  <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Sensory guide coming soon</p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Build My Visit Section */}
        <section ref={buildRef} className="scroll-mt-32">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Build My Visit</h2>
              <p className="text-sm text-slate-600">Get personalised recommendations based on your needs</p>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <p className="text-slate-600 mb-6">
              Tell us about your sensory preferences and we'll suggest the best times and areas for your visit.
            </p>

            {/* Sensitivity Selectors */}
            <div className="space-y-6 mb-8">
              {/* Noise Sensitivity */}
              <div>
                <Label className="flex items-center gap-2 mb-3 text-slate-700">
                  <Volume2 className="w-4 h-4" />
                  Noise sensitivity
                </Label>
                <div className="flex gap-2">
                  {['low', 'medium', 'high'].map((level) => (
                    <button
                      key={level}
                      onClick={() => setSensitivities(prev => ({ ...prev, noise: level }))}
                      className={`flex-1 py-3 px-4 rounded-xl text-sm font-medium transition-all ${
                        sensitivities.noise === level
                          ? 'bg-teal-600 text-white shadow-md'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Crowd Sensitivity */}
              <div>
                <Label className="flex items-center gap-2 mb-3 text-slate-700">
                  <Users className="w-4 h-4" />
                  Crowd sensitivity
                </Label>
                <div className="flex gap-2">
                  {['low', 'medium', 'high'].map((level) => (
                    <button
                      key={level}
                      onClick={() => setSensitivities(prev => ({ ...prev, crowd: level }))}
                      className={`flex-1 py-3 px-4 rounded-xl text-sm font-medium transition-all ${
                        sensitivities.crowd === level
                          ? 'bg-teal-600 text-white shadow-md'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Lighting Sensitivity */}
              <div>
                <Label className="flex items-center gap-2 mb-3 text-slate-700">
                  <Sun className="w-4 h-4" />
                  Lighting sensitivity
                </Label>
                <div className="flex gap-2">
                  {['low', 'medium', 'high'].map((level) => (
                    <button
                      key={level}
                      onClick={() => setSensitivities(prev => ({ ...prev, lighting: level }))}
                      className={`flex-1 py-3 px-4 rounded-xl text-sm font-medium transition-all ${
                        sensitivities.lighting === level
                          ? 'bg-teal-600 text-white shadow-md'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <Button 
              onClick={generateRecommendations}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl h-12 text-base"
            >
              Get my recommendations
            </Button>

            {/* Recommendations Display */}
            {recommendations && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-8 space-y-6"
              >
                {/* Best Times */}
                <div className="bg-emerald-50 rounded-xl p-5">
                  <h3 className="font-semibold text-emerald-800 flex items-center gap-2 mb-3">
                    <Clock className="w-5 h-5" />
                    Best times to visit
                  </h3>
                  <ul className="space-y-2">
                    {recommendations.bestTimes.map((time, i) => (
                      <li key={i} className="flex items-center gap-2 text-emerald-700">
                        <CheckCircle className="w-4 h-4 flex-shrink-0" />
                        <span>{time}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Recommended Areas */}
                {recommendations.recommendedAreas.length > 0 && (
                  <div className="bg-blue-50 rounded-xl p-5">
                    <h3 className="font-semibold text-blue-800 flex items-center gap-2 mb-3">
                      <CheckCircle className="w-5 h-5" />
                      Recommended areas for you
                    </h3>
                    <ul className="space-y-2">
                      {recommendations.recommendedAreas.map((area, i) => (
                        <li key={i} className="flex items-center gap-2 text-blue-700">
                          <MapPin className="w-4 h-4 flex-shrink-0" />
                          <span>{area}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Caution Areas */}
                {recommendations.cautionAreas.length > 0 && (
                  <div className="bg-amber-50 rounded-xl p-5">
                    <h3 className="font-semibold text-amber-800 flex items-center gap-2 mb-3">
                      <AlertTriangle className="w-5 h-5" />
                      Areas to be aware of
                    </h3>
                    <ul className="space-y-2">
                      {recommendations.cautionAreas.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-amber-700">
                          <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <span>
                            <strong>{item.name}</strong>
                            <span className="text-amber-600"> — may have {item.concerns.join(', ')}</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Tips */}
                {recommendations.tips.length > 0 && (
                  <div className="bg-slate-100 rounded-xl p-5">
                    <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-3">
                      <Info className="w-5 h-5" />
                      Helpful tips
                    </h3>
                    <ul className="space-y-2">
                      {recommendations.tips.map((tip, i) => (
                        <li key={i} className="flex items-center gap-2 text-slate-600">
                          <span className="w-1.5 h-1.5 bg-slate-400 rounded-full flex-shrink-0" />
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </section>

        {/* Quick Info */}
        {visitRecs.accessibility_features && visitRecs.accessibility_features.length > 0 && (
          <section className="bg-teal-50 rounded-2xl p-6">
            <h3 className="font-semibold text-teal-800 mb-4">Accessibility Features</h3>
            <ul className="grid gap-2 sm:grid-cols-2">
              {visitRecs.accessibility_features.map((feature, i) => (
                <li key={i} className="flex items-center gap-2 text-teal-700">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm">{feature}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 bg-white border-t border-slate-200 mt-12">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-slate-600 text-sm">
            <Compass className="w-5 h-5 text-teal-600" />
            <span>Nysa Sensory Experiences</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <Link to="/explore" className="hover:text-slate-700">Explore More</Link>
            <Link to="/terms" className="hover:text-slate-700">Terms</Link>
            <Link to="/privacy" className="hover:text-slate-700">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ExperienceViewer;
